import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, TrendingUp, DollarSign, Activity } from "lucide-react";
import { OverviewTab } from "./tabs/OverviewTab";
import { useTranslation } from "react-i18next";
import { useState, lazy, Suspense, useEffect, useMemo } from "react";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { DashboardTab } from "@/config/dashboardWidgets";

// Lazy load non-critical tabs
const PerformanceTab = lazy(() => import("./tabs/PerformanceTab").then(m => ({ default: m.PerformanceTab })));
const FinancesTab = lazy(() => import("./tabs/FinancesTab").then(m => ({ default: m.FinancesTab })));
const ActivityTab = lazy(() => import("./tabs/ActivityTab").then(m => ({ default: m.ActivityTab })));

// Fallback component for lazy loaded tabs
function TabLoading() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

interface TabDefinition {
  id: DashboardTab;
  icon: typeof BarChart3;
  labelKey: string;
  shortLabelKey: string;
}

const ALL_TABS: TabDefinition[] = [
  { id: 'overview', icon: BarChart3, labelKey: "tabs.overview", shortLabelKey: "tabs.shortOverview" },
  { id: 'performance', icon: TrendingUp, labelKey: "tabs.performance", shortLabelKey: "tabs.shortPerformance" },
  { id: 'finances', icon: DollarSign, labelKey: "tabs.finances", shortLabelKey: "tabs.shortFinances" },
  { id: 'activity', icon: Activity, labelKey: "tabs.activity", shortLabelKey: "tabs.shortActivity" },
];

export function DashboardTabs() {
  const { t } = useTranslation("dashboard");
  const { hasVisibleWidgetsForTab, config } = useDashboardConfigStore();
  const [activeTab, setActiveTab] = useState<DashboardTab>("overview");

  // Filter tabs based on visible widgets
  // Include config.widgets in deps to force recalculation when config changes
  const visibleTabs = useMemo(() => {
    return ALL_TABS.filter(tab => hasVisibleWidgetsForTab(tab.id));
  }, [hasVisibleWidgetsForTab, config.widgets]);

  // Auto-switch to first visible tab if current tab becomes invisible
  useEffect(() => {
    const isCurrentTabVisible = visibleTabs.some(tab => tab.id === activeTab);
    if (!isCurrentTabVisible && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  // If no tabs visible, show empty state
  if (visibleTabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <BarChart3 className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          {t("emptyWidgets.title")}
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          {t("emptyWidgets.description")}
        </p>
      </div>
    );
  }

  // Dynamic grid columns based on visible tabs count
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
  }[visibleTabs.length] || 'grid-cols-4';

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DashboardTab)} className="w-full">
        <div className="sticky top-0 z-20 bg-background pb-3 border-b">
          <TabsList className={`grid w-full ${gridColsClass} h-auto`}>
            {visibleTabs.map(tab => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2 px-2 py-2 sm:px-4 sm:py-3"
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t(tab.labelKey)}</span>
                <span className="sm:hidden">{t(tab.shortLabelKey)}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="mt-6">
          <TabsContent value="overview" className="mt-0">
            <OverviewTab />
          </TabsContent>

          <TabsContent value="performance" className="mt-0">
            <Suspense fallback={<TabLoading />}>
              <PerformanceTab />
            </Suspense>
          </TabsContent>

          <TabsContent value="finances" className="mt-0">
            <Suspense fallback={<TabLoading />}>
              <FinancesTab />
            </Suspense>
          </TabsContent>

          <TabsContent value="activity" className="mt-0">
            <Suspense fallback={<TabLoading />}>
              <ActivityTab />
            </Suspense>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
